/**
 * 承諾ページ /o/:token（#233、docs/93 §4.4、Figma ②-1〜②-4）：{vendor}がログインせずに{order}を確かめて「承諾」を押す1枚。
 *   アプリの上の帯とメニューは出さない（ログイン前の画面と同じくライト・製品の既定の色）。スマホ前提＝縦1列・押す所 56px
 *   頭：「発行：自社名」・題（{order}）・番号・件名・合計（税込）・発行日と承諾期限 → 「約款 ›」と「PDF 保存」は紙の上 → 紙（ページの画像＝pdf.js）
 *   下に固定：［承諾］と小さく「紙で受け取りたい ›」。初回（その{vendor}に電子での承諾の記録が無い）だけボタンの上に説明の2行
 *   承諾のあと：題の右に「承諾済 MM/DD」・「承諾しました（MM/DD HH:MM）」・記録番号・{orderAck}（PDF）。もう一度開いても同じ（押せない）
 *   「紙で受け取りたい」→ 確認の窓（主＝紙で受け取る・脇＝閉じる）→ リンクは無効・発行した会社に連絡
 *   無効・取消し・期限切れ（410）は止めの画面（発行した会社名）。無い鍵（404）は「ページが見つかりません」
 *   送った会社でログイン中の人は確かめ用に見るだけ（押せない＝API も 403）
 *   訂正版（#233 2b、docs/93 §14.5）：前の回の鍵（410 superseded）は止めの画面「新しい{order}が届いています」＋「新しい{order}を開く」（新しい承諾ページ）。
 *     2回目からの承諾ページは説明の2行なし（承諾の記録があるので）・紙の上に「変更点」の表（合計の増減・条件の変更・変えた行・前回の承諾の記録番号）
 */
import { ORDER_PUBLIC_MSG as M, STOP_MSG, fillLabels, DEFAULT_LABELS, type OrderAcceptResult, type OrderPublicPage } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { PageViewer, type ViewerPage, type Zoom } from "../../components/PageViewer.jsx";
import { OrderChanges } from "./revise.jsx";
import { StopScreen } from "../../components/StopScreen.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { renderPdf } from "../../lib/pdfPages.js";
import { PRELOGIN_PRODUCT, productInfo } from "../../lib/product.js";

class OrderApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { title?: string; code?: string; company?: string; state?: string; name?: string; next?: string | null },
  ) {
    super(body.title ?? "処理できませんでした");
  }
}
/** 公開の API（控えを使わない・リファラーを送らない。Cookie は同じサイトなので送る＝送った会社でログイン中かを API が見る） */
async function ocall(path: string, method: "GET" | "POST" = "GET"): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/o/${path}`, { method, credentials: "same-origin", referrerPolicy: "no-referrer", cache: "no-store", ...(method === "POST" ? { headers: { "content-type": "application/json" }, body: "{}" } : {}) });
  } catch {
    throw new OrderApiError(0, { title: "つながりませんでした。電波の状態を確かめて、もう一度お試しください" });
  }
  if (!res.ok) {
    let body: OrderApiError["body"] = {};
    try {
      body = (await res.json()) as OrderApiError["body"];
    } catch {
      /* JSON でない答え */
    }
    throw new OrderApiError(res.status, body);
  }
  return res;
}

const jst = (iso: string) => new Date(Date.parse(iso) + 9 * 3600 * 1000);
const ymdSlash = (iso: string) => {
  const t = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00Z`) : jst(iso);
  return `${t.getUTCFullYear()}/${String(t.getUTCMonth() + 1).padStart(2, "0")}/${String(t.getUTCDate()).padStart(2, "0")}`;
};
const mdTime = (iso: string) => {
  const t = jst(iso);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()} ${String(t.getUTCHours()).padStart(2, "0")}:${String(t.getUTCMinutes()).padStart(2, "0")}`;
};
const mdOnly = (iso: string) => {
  const t = jst(iso);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()}`;
};

/** リファラーを送らない・検索に出さない（Caddy も /o/* に付ける。開発の Vite 用に meta も） */
function useNoReferrer(title: string) {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    const r = document.createElement("meta");
    r.name = "robots";
    r.content = "noindex, nofollow";
    document.head.append(m, r);
    const prev = document.title;
    document.title = title;
    return () => {
      m.remove();
      r.remove();
      document.title = prev;
    };
  }, [title]);
}

export function OrderAcceptPage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const P = productInfo(PRELOGIN_PRODUCT);
  const key = ["order-page", token];
  const q = useQuery({ queryKey: key, queryFn: async () => (await (await ocall(encodeURIComponent(token))).json()) as OrderPublicPage, retry: false, staleTime: Infinity });
  const d = q.data;
  useNoReferrer(d ? `${d.title} ${d.docNo}` : "");
  const [pages, setPages] = useState<ViewerPage[] | null>(null);
  const [pdfError, setPdfError] = useState<string | undefined>();
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [paperAsk, setPaperAsk] = useState(false);
  const [declined, setDeclined] = useState(false);
  const ok = !!d;
  useEffect(() => {
    if (!ok) return;
    let alive = true;
    const urls: string[] = [];
    (async () => {
      try {
        const blob = await (await ocall(`${encodeURIComponent(token)}/pdf?view=1`)).blob(); // 約款は「約款 ›」で開く（並べない）
        const got: ViewerPage[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          urls.push(p.url);
          got[i - 1] = { n: i, url: p.url, width: p.w, height: p.h };
        });
        if (alive) setPages(got.filter(Boolean));
      } catch (e) {
        if (alive) setPdfError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [ok, token]);

  if (q.isError || declined) {
    const e = q.error instanceof OrderApiError ? q.error : null;
    if (declined) return <StopScreen art="mail" title={M.paperTitle} body={M.paperDone.replace("{company}", d?.company ?? "")} testId="order-declined" />;
    // 訂正版を出した前の回の鍵（#233 2b）：新しい承諾ページへ（紙で出した・期限切れなど新しいページが無ければボタンなし）
    if (e?.status === 410 && e.body.state === "superseded") {
      const name = e.body.name || "書類";
      return (
        <StopScreen
          art="mail"
          title={STOP_MSG.orderRevised.title(name)}
          body={STOP_MSG.orderRevised.body(e.body.company ?? "", name, !!e.body.next)}
          primary={e.body.next ? { label: STOP_MSG.orderRevised.open(name), href: e.body.next, testId: "order-revised-open" } : undefined}
          testId="order-revised"
        />
      );
    }
    if (e?.status === 410) return <StopScreen art="lock" title={e.body.state === "expired" ? STOP_MSG.orderGone.expiredTitle : STOP_MSG.orderGone.title} body={STOP_MSG.orderGone.body(e.body.company ?? "")} testId="order-gone" />;
    if (e?.status === 404) return <StopScreen art="question" title={STOP_MSG.deliveryNotFound.title} body={STOP_MSG.deliveryNotFound.body} testId="order-notfound" />;
    return (
      <div className="dview" data-testid="order-accept">
        <ErrorText>{q.error instanceof Error ? q.error.message : String(q.error)}</ErrorText>
      </div>
    );
  }
  if (!d) return <div className="dview"><p className="hint">読み込んでいます…</p></div>;
  const L = { ...DEFAULT_LABELS, order: d.title, orderAck: d.ackTitle };
  const say = (s: string) => fillLabels(s, L).replaceAll("{company}", d.company);
  const accept = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const r = (await (await ocall(`${encodeURIComponent(token)}/accept`, "POST")).json()) as OrderAcceptResult;
      qc.setQueryData(key, { ...d, accepted: r } satisfies OrderPublicPage);
    } catch (e) {
      if (e instanceof OrderApiError && e.status === 409) void q.refetch();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const decline = async () => {
    setBusy(true);
    try {
      await ocall(`${encodeURIComponent(token)}/decline-electronic`, "POST");
      setDeclined(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPaperAsk(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dview oaccept" data-testid="order-accept">
      <header className="dview-head">
        <p className="dview-kicker" data-testid="order-from">{say(M.from)}</p>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <h1 className="dview-company" data-testid="order-title">{d.title}</h1>
          {d.accepted ? <span className="chip ai" data-testid="order-accepted-chip">{M.acceptedChip.replace("{md}", mdOnly(d.accepted.at))}</span> : null}
        </div>
        <p className="dview-subject" data-testid="order-subject">{d.docNo}　{d.subject}</p>
        <p className="oaccept-total">
          <span className="muted">{M.total}</span> <b className="num" data-testid="order-total">{d.total.toLocaleString("ja-JP")}</b> 円
        </p>
        <p className="hint num">
          {M.issueDate} {ymdSlash(d.issueDate)}
          {d.accepted ? "" : `　${M.expires} ${ymdSlash(d.expiresAt)}`}
        </p>
      </header>
      <div className="row oaccept-links">
        {d.hasTerms ? <a className="btn text" href={`/api/v1/o/${encodeURIComponent(token)}/terms`} target="_blank" rel="noreferrer" data-testid="order-terms">{M.terms}</a> : null}
        <a className="btn" href={`/api/v1/o/${encodeURIComponent(token)}/pdf?dl=1`} rel="noreferrer" data-testid="order-pdf">{M.pdf}</a>
      </div>
      {/* 訂正版（#233 2b）：紙の上に「変更点」の表 */}
      {d.changes ? (
        <section className="oaccept-changes" aria-label={M.changes} data-testid="order-public-changes">
          <h2 className="oaccept-h2">{M.changes}</h2>
          <OrderChanges prev={d.changes.prevNo} no={d.docNo} prevTotal={d.changes.prevTotal} total={d.total} recordNo={d.changes.prevRecordNo} terms={d.changes.terms} rows={d.changes.rows} testId="order-public-diff" />
        </section>
      ) : null}
      <div className="oaccept-paper" data-testid="order-paper">
        {pdfError ? <ErrorText>{pdfError}</ErrorText> : null}
        <PageViewer docKey={token} title={d.title} pages={pages ?? undefined} error={undefined} pageCount={d.pages} openUrl={`/api/v1/o/${encodeURIComponent(token)}/pdf`} zoom={zoom} onZoom={setZoom} compact label={d.title} />
      </div>
      {d.accepted ? (
        <div className="oaccept-done" data-testid="order-accepted">
          <b>{M.acceptedAt.replace("{at}", mdTime(d.accepted.at))}</b>
          <span className="muted num" data-testid="order-record-no">{M.recordNo.replace("{no}", d.accepted.recordNo)}</span>
          <a className="btn" href={`/api/v1/o/${encodeURIComponent(token)}/ack.pdf`} target="_blank" rel="noreferrer" data-testid="order-ack-pdf">{say(M.ackPdf)}</a>
        </div>
      ) : (
        <div className="oaccept-bar" data-testid="order-accept-bar" data-notice-above="">
          {d.sender ? <p className="hint" data-testid="order-sender">{M.sender}</p> : null}
          {d.firstTime && !d.sender ? <p className="oaccept-first" data-testid="order-first-time">{say(M.firstTime)}</p> : null}
          <ErrorText>{err}</ErrorText>
          <button type="button" className="btn primary oaccept-btn" disabled={busy || d.sender} onClick={() => void accept()} data-testid="order-accept-btn">
            {busy ? M.accepting : M.accept}
          </button>
          {d.sender ? null : (
            <button type="button" className="btn text" onClick={() => setPaperAsk(true)} data-testid="order-paper-ask">{M.paper}</button>
          )}
        </div>
      )}
      <footer className="dview-foot">
        <img src={P.assets.logoHeader} alt={P.name} width={96} height={30} />
      </footer>
      {paperAsk ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setPaperAsk(false)}>
          <div className="dlg" role="dialog" aria-label={M.paperTitle} data-testid="order-paper-dialog">
            <div className="dh">{M.paperTitle}</div>
            <div className="db">
              <p style={{ margin: 0 }}>{say(M.paperBody)}</p>
              {/* 収入印紙（#252）：請負・税抜 1万円以上だけ */}
              {d.stamp ? <p className="hint" style={{ margin: 0 }} data-testid="order-public-stamp">{say(M.paperStamp)}</p> : null}
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={busy} onClick={() => setPaperAsk(false)}>{M.paperClose}</button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void decline()} data-testid="order-paper-confirm">{M.paperConfirm}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
